/**
 * Per-ground and per-field career figures for the grounds map, from
 * statsCompute's `by_ground`/`by_facility` (never recomputed here). Moved out of
 * GroundsMap.jsx so it can be unit-tested without mounting the map.
 */

/** Issue #582 — the per-field (facility) breakdown for one venue, merged
 * from statsCompute's batting/bowling `by_facility` (never recomputed here).
 * Grouped by ground name, then by facility_id. The NULL facility_id bucket —
 * most historic matches plus every "Not sure" pick, deliberately
 * un-backfilled (#546) — is kept DISTINCT under a reserved key so it renders
 * as its own honest "Field not recorded" row rather than being dropped or
 * folded into a named field. Module level for the max-lines ratchet, same as
 * `buildStatsByName`. Returns Map<lowerGroundName, facilityRow[]>. */
function buildFacilityStats(career) {
  const NULL_KEY = "__not_recorded__";
  const byGround = new Map(); // lowerGroundName -> Map<facKey, row>
  const rowFor = (groundName, r) => {
    const gk = (groundName || "").toLowerCase();
    if (!byGround.has(gk)) byGround.set(gk, new Map());
    const facMap = byGround.get(gk);
    const fk = r.facility_id ?? NULL_KEY;
    if (!facMap.has(fk)) {
      facMap.set(fk, {
        facility_id: r.facility_id ?? null,
        facility_name: r.facility_name ?? null,
        facility_surface: r.facility_surface ?? null,
      });
    }
    return facMap.get(fk);
  };
  (career?.batting?.by_facility || []).forEach((r) => {
    const row = rowFor(r.ground, r);
    row.runs = r.runs;
    row.batting_matches = r.matches;
  });
  (career?.bowling?.by_facility || []).forEach((r) => {
    const row = rowFor(r.ground, r);
    row.wickets = r.wickets;
    row.runs_conceded = r.runs_conceded;
    row.overs = r.overs;
    // #860/D260: the API's economy is over balls and over the readable spells only.
    row.economy = r.econ ?? null;
    row.overs_left_out = r.overs_left_out ?? 0;
    row.bowling_matches = r.matches;
  });
  const out = new Map();
  byGround.forEach((facMap, key) => {
    const rows = Array.from(facMap.values()).sort((a, b) => {
      // The null "not recorded" bucket always sorts LAST — it is context, not
      // a field with a name to rank.
      const aNull = a.facility_id === null;
      const bNull = b.facility_id === null;
      if (aNull !== bNull) return aNull ? 1 : -1;
      const am = a.batting_matches ?? a.bowling_matches ?? 0;
      const bm = b.batting_matches ?? b.bowling_matches ?? 0;
      return bm - am;
    });
    out.set(key, rows);
  });
  return out;
}

/** Per-ground career record, keyed by lowercased ground name — pure, from
 * statsCompute's by_ground (never recomputed here). Module level for the
 * maintainability budget (`eslint.config.mjs`): GroundsMap itself sits at
 * the max-lines ratchet. */
export function buildStatsByName(career) {
  const m = new Map();
  (career?.batting?.by_ground || []).forEach((row) => {
    const key = (row.ground || "").toLowerCase();
    m.set(key, { ...(m.get(key) || {}), runs: row.runs, batting_matches: row.matches });
  });
  (career?.bowling?.by_ground || []).forEach((row) => {
    const key = (row.ground || "").toLowerCase();
    const economy = row.econ ?? null;
    m.set(key, {
      ...(m.get(key) || {}),
      wickets: row.wickets,
      runs_conceded: row.runs_conceded,
      overs: row.overs,
      economy,
      overs_left_out: row.overs_left_out ?? 0,
      bowling_matches: row.matches,
    });
  });
  // #582 — attach each venue's per-field breakdown alongside its totals.
  buildFacilityStats(career).forEach((facilities, key) => {
    m.set(key, { ...(m.get(key) || {}), facilities });
  });
  return m;
}
